import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {ROUTES} from '../routes';
import {Link, useNavigate} from 'react-router-dom';
import {useNotification} from '../hooks/use-notifications.tsx';
import {useAsync} from 'react-use';
import {SettingsService} from '../api/generated/SettingsService.ts';
import {useCallback, useMemo} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {settingSchema, SettingSchema} from './schemas.ts';
import {createForm} from '../forms';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {BasicPage} from '../components/basic-page.tsx';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {Button, Group} from '@mantine/core';

const Form = createForm<SettingSchema>();

export const SettingDetails = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.SETTINGS.DETAIL);
  const navigate = useNavigate();
  const notifications = useNotification();

  const fetchSetting = useAsync(async () => {
    const {data} = await SettingsService.getById({id});
    return data;
  }, [id]);

  const onSubmit = useCallback<SubmitHandler<SettingSchema>>(
    async (values) => {
      const response = await SettingsService.save({
        id,
        body: values,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Setting updated');
      navigate(ROUTES.ADMIN.SETTINGS.INDEX.path);
    },
    [id, navigate, notifications]
  );

  const setting = fetchSetting.value || undefined;
  const settingKey = setting?.key ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Settings', href: ROUTES.ADMIN.SETTINGS.INDEX.path},
      {title: settingKey},
    ],
    [settingKey]
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchSetting}>
        <Form
          schema={settingSchema}
          onSubmit={onSubmit}
          defaultValues={setting}
        >
          <Form.Container>
            <Form.TextInput name="value" label="Value" />
            <Group mt={24} justify={'flex-end'}>
              <Button
                type="button"
                variant="outline"
                component={Link}
                to={ROUTES.ADMIN.SETTINGS.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update Setting</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};